import { Show, DateField } from "@refinedev/antd";
import {
  Alert,
  Button,
  Card,
  Divider,
  Result,
  Space,
  Spin,
  Typography,
} from "antd";
import {
  IdcardOutlined,
  SafetyCertificateOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  LockOutlined,
  UserOutlined,
  LoginOutlined,
  FileSearchOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  useApiUrl,
  useCustom,
  useCustomMutation,
  useNotification,
  useLogout,
} from "@refinedev/core";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, CSSProperties, ReactNode } from "react";
import { IVesselDataVerification } from "interfaces";
import { ShowSection, ShowField, ShowFieldGrid } from "components/show";
import { getVerificationStatusColor } from "constants/tagColors";
import { VerificationStatusTag } from "./components/VerificationStatusTag";
import { SubscriptionSnapshotView } from "./components/SubscriptionSnapshotView";
import { NotApproveModal } from "./components/NotApproveModal";
import { colors, shadows } from "config/theme";

const { Title, Text } = Typography;

const DATE_FORMAT = "DD-MMM-YYYY HH:mm";

type ApproveOutcome =
  | { kind: "idle" }
  | { kind: "forbidden" }
  | { kind: "conflict";  approverEmail?: string; approvedAt?: string }
  | { kind: "error";     message: string };

type NotApproveOutcome =
  | { kind: "idle" }
  | { kind: "validation"; message: string }
  | { kind: "forbidden" }
  | { kind: "conflict";   approverEmail?: string; approvedAt?: string }
  | { kind: "error";      message: string };

const readErrorBody = (err: any): Record<string, any> =>
  err?.response?.data ?? err?.errors ?? {};

const readErrorStatus = (err: any): number | undefined =>
  err?.statusCode ?? err?.response?.status;

const styles: Record<string, CSSProperties> = {
  heroCard: {
    borderRadius: 12,
    border: "none",
    boxShadow: shadows.sm,
    overflow: "hidden",
  },
  heroBand: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    padding: 24,
    background: `linear-gradient(135deg, ${colors.containerBg} 0%, ${colors.bgBase} 70%)`,
  },
  heroLeft: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  heroIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 48,
    height: 48,
    borderRadius: 12,
    background: colors.bgBase,
    color: colors.siderBg,
    fontSize: 22,
    boxShadow: shadows.sm,
    flexShrink: 0,
  },
  heroTitle: {
    fontSize: 22,
    fontWeight: 600,
    color: colors.textPrimary,
    margin: 0,
    lineHeight: 1.3,
  },
  heroSubtitle: {
    fontSize: 14,
    color: colors.textTertiary,
  },
  heroAction: {
    padding: 24,
    borderTop: `1px solid ${colors.borderLight}`,
    background: colors.bgBase,
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  windowHint: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: colors.textTertiary,
    fontSize: 13,
  },
  bannerWrap: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "16px 20px",
    borderRadius: 10,
  },
  bannerTextGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  bannerTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  bannerSub: {
    fontSize: 13,
    color: colors.textTertiary,
  },
  bannerSubStrong: {
    fontSize: 13,
    fontWeight: 500,
    color: colors.textSecondary,
  },
  gateOuter: {
    minHeight: "60vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  gateCard: {
    maxWidth: 480,
    width: "100%",
    borderRadius: 16,
    border: "none",
    boxShadow: shadows.md,
  },
  gateInner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    textAlign: "center",
  },
  gateMedallion: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 72,
    height: 72,
    borderRadius: "50%",
    background: colors.containerBg,
    color: colors.siderBg,
    fontSize: 30,
  },
  gateTitleGroup: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
  },
  gateTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600,
    color: colors.textPrimary,
  },
  gateMessage: {
    fontSize: 14,
    color: colors.textSecondary,
    maxWidth: 360,
    lineHeight: 1.6,
  },
  gateAccount: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    background: colors.bgMuted,
    borderRadius: 8,
  },
  gateAccountText: {
    fontSize: 13,
    color: colors.textSecondary,
  },
  gateHelp: {
    fontSize: 13,
    color: colors.textSecondary,
    maxWidth: 400,
    lineHeight: 1.6,
  },
  gateActions: {
    justifyContent: "center",
    marginTop: 4,
  },
};

export const VesselDataVerificationApprove: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const apiUrl = useApiUrl();
  const navigate = useNavigate();
  const { open } = useNotification();
  const { mutate: logout } = useLogout();

  const {
    result,
    query: { isLoading, error: getError, refetch },
  } = useCustom<IVesselDataVerification>({
    url: `${apiUrl}/vessel-data-verifications/by-public-id/${publicId}`,
    method: "get",
    errorNotification: false,
  });

  const record = result?.data;

  const {
    mutate: approve,
    mutation: { isPending: isApproving, error: approveError, isSuccess: approveSucceeded },
  } = useCustomMutation<IVesselDataVerification>();

  const [rejectOpen, setRejectOpen] = useState(false);

  const {
    mutate: notApprove,
    mutation: { isPending: isRejecting, error: notApproveError, reset: resetNotApprove },
  } = useCustomMutation<IVesselDataVerification>();

  const approveOutcome: ApproveOutcome = (() => {
    if (!approveError) return { kind: "idle" };
    const status = readErrorStatus(approveError);
    const body = readErrorBody(approveError);
    if (status === 403) return { kind: "forbidden" };
    if (status === 409) return { kind: "conflict", approverEmail: body.approverEmail, approvedAt: body.approvedAt };
    return { kind: "error", message: (approveError as any)?.message ?? "Approval failed" };
  })();

  const notApproveOutcome: NotApproveOutcome = (() => {
    if (!notApproveError) return { kind: "idle" };
    const status = readErrorStatus(notApproveError);
    const body = readErrorBody(notApproveError);
    if (status === 400) {
      const message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
      return { kind: "validation", message: message ?? "Invalid reason" };
    }
    if (status === 403) return { kind: "forbidden" };
    if (status === 409) return { kind: "conflict", approverEmail: body.approverEmail, approvedAt: body.approvedAt };
    return { kind: "error", message: (notApproveError as any)?.message ?? "Not approve failed" };
  })();

  useEffect(() => {
    if (approveOutcome.kind === "conflict" || notApproveOutcome.kind === "conflict") {
      refetch();
    }
  }, [approveOutcome.kind, notApproveOutcome.kind, refetch]);

  const handleApprove = () => {
    approve(
      {
        url: `${apiUrl}/vessel-data-verifications/by-public-id/${publicId}/approve`,
        method: "post",
        values: {},
        errorNotification: false,
      },
      {
        onSuccess: () => {
          open?.({ type: "success", message: "Vessel data approved" });
          refetch();
        },
      },
    );
  };

  const openRejectModal = () => {
    resetNotApprove();
    setRejectOpen(true);
  };

  const handleNotApprove = (reason: string) => {
    notApprove(
      {
        url: `${apiUrl}/vessel-data-verifications/by-public-id/${publicId}/not-approve`,
        method: "post",
        values: { reason },
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setRejectOpen(false);
          open?.({ type: "success", message: "Vessel data not approved — vessel placed on hold" });
          refetch();
        },
        onError: (err) => {
          if (readErrorStatus(err) !== 400) {
            setRejectOpen(false);
          }
        },
      },
    );
  };

  const getStatus = readErrorStatus(getError);

  if (isLoading) {
    return (
      <div style={{ padding: 64, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (getStatus === 404) {
    return (
      <div style={styles.gateOuter}>
        <Card style={styles.gateCard} styles={{ body: { padding: 40 } }}>
          <div style={styles.gateInner}>
            <div style={styles.gateMedallion}>
              <FileSearchOutlined />
            </div>
            <div style={styles.gateTitleGroup}>
              <Title level={3} style={styles.gateTitle}>
                Verification not found
              </Title>
              <Text style={styles.gateMessage}>
                The link may be invalid or the verification may have been removed.
              </Text>
            </div>
            <Text style={styles.gateHelp}>
              Open the most recent verification email to get a valid link, or return to the list to
              find the verification you need.
            </Text>
            <Space size={12} wrap style={styles.gateActions}>
              <Button type="primary" size="large" onClick={() => navigate("/vessel-verifications")}>
                Back to list
              </Button>
              <Button size="large" onClick={() => navigate("/dashboard")}>
                Go to dashboard
              </Button>
            </Space>
          </div>
        </Card>
      </div>
    );
  }

  if (getStatus === 403) {
    const signedInEmail = localStorage.getItem("emr-email") || undefined;
    return (
      <div style={styles.gateOuter}>
        <Card style={styles.gateCard} styles={{ body: { padding: 40 } }}>
          <div style={styles.gateInner}>
            <div style={styles.gateMedallion}>
              <LockOutlined />
            </div>
            <div style={styles.gateTitleGroup}>
              <Title level={3} style={styles.gateTitle}>
                Not authorized
              </Title>
              <Text style={styles.gateMessage}>
                Your account is not authorized to approve vessel data verifications.
              </Text>
            </div>
            {signedInEmail && (
              <span style={styles.gateAccount}>
                <UserOutlined style={{ color: colors.textTertiary }} />
                <Text style={styles.gateAccountText}>Signed in as {signedInEmail}</Text>
              </span>
            )}
            <Text style={styles.gateHelp}>
              This approval link is intended for a designated approver. If you reached it in error,
              sign in with the authorized account or contact your administrator.
            </Text>
            <Space size={12} wrap style={styles.gateActions}>
              <Button type="primary" size="large" onClick={() => navigate("/dashboard")}>
                Go to dashboard
              </Button>
              <Button size="large" icon={<LoginOutlined />} onClick={() => logout()}>
                Sign in with a different account
              </Button>
            </Space>
          </div>
        </Card>
      </div>
    );
  }

  if (getError || !record) {
    return (
      <Result
        status="error"
        title="Could not load verification"
        extra={<Button onClick={() => refetch()}>Retry</Button>}
      />
    );
  }

  const approvedBy = (email?: string | null, at?: string | null): ReactNode => {
    if (!email) return null;
    return (
      <Text style={styles.bannerSub}>
        Approved by {email}
        {at && (
          <>
            {" on "}
            <DateField value={at} format={DATE_FORMAT} style={styles.bannerSubStrong} />
          </>
        )}
      </Text>
    );
  };

  const renderBanner = (
    tone: "success" | "error",
    icon: ReactNode,
    title: string,
    sub: ReactNode,
  ) => {
    const toned =
      tone === "success"
        ? { background: colors.successBg, border: `1px solid ${colors.successBorder}` }
        : { background: colors.errorBg, border: `1px solid ${colors.errorBorder}` };
    return (
      <div style={{ ...styles.bannerWrap, ...toned }}>
        {icon}
        <div style={styles.bannerTextGroup}>
          <Text style={styles.bannerTitle}>{title}</Text>
          {sub}
        </div>
      </div>
    );
  };

  const renderApprovalPanel = () => {
    if (approveSucceeded) {
      return renderBanner(
        "success",
        <CheckCircleFilled style={{ fontSize: 30, color: colors.success }} />,
        "Vessel data approved",
        approvedBy(record.approverEmail, record.approvedAt),
      );
    }

    if (approveOutcome.kind === "forbidden") {
      return <Alert type="error" showIcon message="Your account is not authorized to approve vessel data verifications." />;
    }
    if (approveOutcome.kind === "conflict") {
      return <Alert
        type="warning"
        showIcon
        message="Already approved"
        description={`Approved by ${approveOutcome.approverEmail ?? "another user"}${approveOutcome.approvedAt ? ` on ${approveOutcome.approvedAt}` : ""}.`}
      />;
    }
    if (notApproveOutcome.kind === "forbidden") {
      return <Alert type="error" showIcon message="Your account is not authorized to approve vessel data verifications." />;
    }
    if (notApproveOutcome.kind === "conflict") {
      return <Alert
        type="warning"
        showIcon
        message="Already approved"
        description={`Approved by ${notApproveOutcome.approverEmail ?? "another user"}${notApproveOutcome.approvedAt ? ` on ${notApproveOutcome.approvedAt}` : ""}. The verification can no longer be rejected.`}
      />;
    }
    if (record.status === "approved") {
      return renderBanner(
        "success",
        <CheckCircleFilled style={{ fontSize: 30, color: colors.success }} />,
        "Already approved",
        approvedBy(record.approverEmail, record.approvedAt),
      );
    }
    const onHold = record.status === "not_approved";

    return (
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {approveOutcome.kind === "error" && (
          <Alert type="error" showIcon message="Approval failed" description={approveOutcome.message} />
        )}
        {notApproveOutcome.kind === "error" && (
          <Alert type="error" showIcon message="Not approve failed" description={notApproveOutcome.message} />
        )}
        {onHold ? (
          <Alert
            type="warning"
            showIcon
            message={record.notApprovedReason ? "Vessel on hold — not approved" : "Vessel on hold"}
            description={
              record.notApprovedReason ? (
                <Space direction="vertical" size={4}>
                  <Text style={{ whiteSpace: "pre-wrap" }}>{record.notApprovedReason}</Text>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Placed on hold by {record.notApprovedByEmail ?? "unknown"}
                    {record.notApprovedAt && (
                      <>
                        {" on "}
                        <DateField value={record.notApprovedAt} format={DATE_FORMAT} />
                      </>
                    )}
                    . Approving lifts the hold.
                  </Text>
                </Space>
              ) : (
                "The approval due date has passed and the vessel was placed on hold. Approving now lifts the hold."
              )
            }
          />
        ) : (
          <Alert
            type="info"
            showIcon
            message="Please review the vessel and subscription details below. Approve once you are satisfied the data is correct."
          />
        )}
        <div style={styles.actionRow}>
          <span style={styles.windowHint}>
            <ClockCircleOutlined />
            <Text style={{ color: colors.textTertiary, fontSize: 13 }}>
              {onHold ? "On hold since " : "Approve by "}
              <DateField value={onHold ? record.notApprovedAt ?? record.expiresAt : record.expiresAt} format={DATE_FORMAT} style={{ color: colors.textSecondary, fontWeight: 500 }} />
              {onHold ? "" : " to avoid the vessel being placed on hold"}
            </Text>
          </span>
          <Space size={12} wrap>
            <Button
              danger
              size="large"
              icon={<StopOutlined />}
              disabled={isApproving}
              onClick={openRejectModal}
            >
              Not Approved
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<CheckCircleOutlined />}
              loading={isApproving}
              disabled={isRejecting}
              onClick={handleApprove}
            >
              Approve Vessel Data
            </Button>
          </Space>
        </div>
      </Space>
    );
  };

  return (
    <Show
      breadcrumb={false}
      isLoading={false}
      headerProps={{
        title: "Vessel Data Verification",
        onBack: () => navigate("/vessel-verifications"),
        ghost: true,
        footer: <Divider plain>© {new Date().getFullYear()} e-Mariner NavSolutions</Divider>,
      }}
    >
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <Card style={styles.heroCard} styles={{ body: { padding: 0 } }}>
          <div style={styles.heroBand}>
            <div style={styles.heroLeft}>
              <div style={styles.heroIcon}>
                <IdcardOutlined />
              </div>
              <div>
                <Title level={3} style={styles.heroTitle}>
                  {record.vesselName}
                </Title>
                <Text style={styles.heroSubtitle}>
                  IMO {record.imoNumber} · {record.shipcoName}
                </Text>
              </div>
            </div>
            <VerificationStatusTag status={record.status} />
          </div>
          <div style={styles.heroAction}>{renderApprovalPanel()}</div>
        </Card>

        <ShowSection
          title="Verification Details"
          subtitle="Vessel, shipco, and licence details"
          icon={<SafetyCertificateOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="Vessel" value={record.vesselName} />
            <ShowField label="IMO Number" value={record.imoNumber} />
            <ShowField label="Shipco" value={record.shipcoName} />
            <ShowField label="UKHO Licence" value={record.ukhoLicenceId} />
            <ShowField label="Sent" value={record.sentAt} type="date" dateFormat={DATE_FORMAT} />
            <ShowField label="Approve By" value={record.expiresAt} type="date" dateFormat={DATE_FORMAT} />
            <ShowField
              label="Status"
              value={record.status === "not_approved" ? "on hold" : record.status}
              type="status"
              tagColor={getVerificationStatusColor(record.status)}
            />
            <ShowField label="Approver" value={record.approverEmail ?? undefined} empty="—" />
            <ShowField label="Approved" value={record.approvedAt ?? undefined} type="date" dateFormat={DATE_FORMAT} empty="—" />
            {record.notApprovedReason != null && (
              <ShowField label="On Hold Reason" value={record.notApprovedReason} type="notes" />
            )}
            {record.notApprovedByEmail != null && (
              <ShowField label="On Hold By" value={record.notApprovedByEmail} />
            )}
            {record.notApprovedAt != null && (
              <ShowField label="On Hold At" value={record.notApprovedAt} type="date" dateFormat={DATE_FORMAT} />
            )}
          </ShowFieldGrid>
        </ShowSection>

        <ShowSection
          title="Subscription Snapshot"
          subtitle="Details captured at verification send time"
          icon={<FileTextOutlined />}
          variant="elevated"
        >
          <SubscriptionSnapshotView data={record.subscriptionSnapshot} />
        </ShowSection>

        <NotApproveModal
          open={rejectOpen}
          initialReason={record.notApprovedReason ?? ""}
          confirmLoading={isRejecting}
          errorMessage={notApproveOutcome.kind === "validation" ? notApproveOutcome.message : undefined}
          onConfirm={handleNotApprove}
          onCancel={() => setRejectOpen(false)}
        />
      </Space>
    </Show>
  );
};
